"use client";

import { useState, useMemo } from "react";
import { useLocation, useNavigate, Link } from "react-router";
import { createClient } from "@/lib/supabase/client";
import { useAccount } from "@/contexts/account-context";
import { toast } from "sonner";
import {
  LayoutDashboard,
  BrainCircuit,
  Megaphone,
  Target,
  FileText,
  Palette,
  MapPin,
  Copy,
  Bot,
  BarChart3,
  TrendingUp,
  Lightbulb,
  Settings,
  Settings2,
  BookOpen,
  Bell,
  BellRing,
  SlidersHorizontal,
  Users,
  Users2,
  ExternalLink,
  Zap,
  GitBranch,
  LogOut,
  SearchCode,
  ChevronRight,
  Key,
  Package,
  Layers,
  BookMarked,
  ClipboardCheck,
  CalendarClock,
  CalendarDays,
  ScanSearch,
  Paintbrush,
  Sparkles,
  Wand2,
  type LucideIcon,
} from "lucide-react";
import * as Collapsible from "@radix-ui/react-collapsible";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { AccountSwitcher } from "@/components/account-switcher";
import { cn } from "@/lib/utils";
import type { AdPlatform } from "@/contexts/account-context";

interface NavItem {
  title: string;
  icon: LucideIcon;
  href: string;
  badge?: string;
  disabled?: boolean;
}

interface NavGroup {
  label: string;
  icon: LucideIcon;
  items: NavItem[];
  defaultOpen?: boolean;
}

const metaCampaignItems: NavItem[] = [
  { title: "Campanha Inteligente", icon: Wand2, href: "/smart-campaign" },
  { title: "Campanhas", icon: Megaphone, href: "/campaigns" },
  { title: "DCO", icon: Layers, href: "/dco" },
  { title: "Ad Sets", icon: Target, href: "/adsets" },
  { title: "Anuncios", icon: FileText, href: "/ads" },
  { title: "Criativos", icon: Palette, href: "/creatives" },
  { title: "Creative Studio", icon: Sparkles, href: "/creative-studio" },
  { title: "Studio IA", icon: Paintbrush, href: "/creatives-studio" },
  { title: "Studio Pro", icon: Palette, href: "/studio-pro" },
  { title: "Post Rápido", icon: Sparkles, href: "/post-rapido" },
  { title: "Templates", icon: Copy, href: "/templates" },
  { title: "Unidades", icon: MapPin, href: "/locations" },
];

const googleCampaignItems: NavItem[] = [
  { title: "Campanhas", icon: SearchCode, href: "/google-ads" },
  { title: "Ad Groups", icon: Target, href: "/google-ads/ad-groups" },
  { title: "Keywords", icon: Key, href: "/google-ads/keywords" },
  { title: "Assets", icon: Package, href: "/google-ads/assets" },
  { title: "Studio IA", icon: Paintbrush, href: "/creatives-studio" },
  { title: "Studio Pro", icon: Palette, href: "/studio-pro" },
  { title: "Post Rápido", icon: Sparkles, href: "/post-rapido" },
  { title: "Templates", icon: Copy, href: "/templates" },
  { title: "Unidades", icon: MapPin, href: "/locations" },
];

function buildNavGroups(platform: AdPlatform): NavGroup[] {
  const campaignItems = platform === "google" ? googleCampaignItems : metaCampaignItems;

  return [
    {
      label: "Visao Geral",
      icon: LayoutDashboard,
      defaultOpen: true,
      items: [
        { title: "Dashboard", icon: LayoutDashboard, href: "/dashboard" },
      ],
    },
    {
      label: "Campanhas",
      icon: Megaphone,
      defaultOpen: true,
      items: campaignItems,
    },
    {
      // Grupo proprio, e nao um item dentro de Campanhas: a midia paga veio do
      // Pure Hub como modulo inteiro, com as duas telas que ela tinha la.
      label: "Midia paga",
      icon: BrainCircuit,
      defaultOpen: true,
      items: [
        { title: "Cérebro", icon: BrainCircuit, href: "/midia-paga" },
        { title: "Análise", icon: Sparkles, href: "/midia-paga/analise" },
      ],
    },
    {
      label: "Inteligencia",
      icon: Bot,
      defaultOpen: true,
      items: [
        { title: "AI Chat", icon: Bot, href: "/ai" },
        { title: "Insights", icon: BarChart3, href: "/insights", badge: "em breve", disabled: true },
        { title: "Tendencias", icon: TrendingUp, href: "/trends" },
        { title: "Tracking", icon: ScanSearch, href: "/tracking" },
        { title: "Audiencias", icon: Users, href: "/audience-insights", badge: "em breve", disabled: true },
        { title: "Atribuicao", icon: GitBranch, href: "/attribution", badge: "em breve", disabled: true },
        { title: "Landing Pages", icon: ExternalLink, href: "/landing-pages", badge: "em breve", disabled: true },
      ],
    },
    {
      label: "Automacao",
      icon: Zap,
      items: [
        { title: "Regras", icon: Settings2, href: "/rules", badge: "em breve", disabled: true },
        { title: "Controle", icon: SlidersHorizontal, href: "/campaign-controls", badge: "em breve", disabled: true },
        { title: "Acoes Auto.", icon: Zap, href: "/auto-actions", badge: "em breve", disabled: true },
        { title: "Otimizacoes", icon: Lightbulb, href: "/optimization", badge: "em breve", disabled: true },
        { title: "Alertas", icon: Bell, href: "/alerts", badge: "em breve", disabled: true },
        { title: "Planejamento", icon: CalendarDays, href: "/planning" },
        { title: "Aprovacoes", icon: ClipboardCheck, href: "/approvals" },
        { title: "Agenda", icon: CalendarClock, href: "/scheduler" },
      ],
    },
    {
      label: "Relatorios",
      icon: BarChart3,
      items: [
        { title: "Agendados", icon: FileText, href: "/scheduled-reports" },
        { title: "Biblioteca", icon: Palette, href: "/creative-library" },
        { title: "Notificacoes", icon: BellRing, href: "/webhooks" },
      ],
    },
    {
      label: "Configuracoes",
      icon: Settings,
      items: [
        { title: "Conhecimento", icon: BookMarked, href: "/knowledge" },
        { title: "Equipe", icon: Users2, href: "/team" },
        { title: "Ajustes", icon: Settings, href: "/settings" },
        { title: "Docs", icon: BookOpen, href: "/docs" },
      ],
    },
  ];
}

function CollapsibleNavGroup({ group }: { group: NavGroup }) {
  const { pathname } = useLocation();
  const hasActiveItem = group.items.some((item) => pathname === item.href);
  const [open, setOpen] = useState(group.defaultOpen || hasActiveItem);

  // Single-item groups render without collapsible
  if (group.items.length === 1) {
    const item = group.items[0];
    return (
      <SidebarMenuItem>
        <SidebarMenuButton
          asChild
          isActive={pathname === item.href}
          tooltip={item.title}
        >
          <Link to={item.href}>
            <item.icon />
            <span>{item.title}</span>
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }

  return (
    <Collapsible.Root open={open} onOpenChange={setOpen}>
      <SidebarMenuItem>
        <Collapsible.Trigger asChild>
          <button
            className={cn(
              "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium transition-colors",
              "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
              "text-sidebar-foreground/70",
              hasActiveItem && "text-sidebar-foreground"
            )}
          >
            <group.icon className="size-4 shrink-0" />
            <span className="flex-1 text-left">{group.label}</span>
            <ChevronRight
              className={cn(
                "size-3.5 shrink-0 transition-transform duration-200",
                open && "rotate-90"
              )}
            />
          </button>
        </Collapsible.Trigger>

        <Collapsible.Content className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
          <SidebarMenu className="ml-4 border-l border-sidebar-border pl-2 mt-0.5">
            {group.items.map((item) => (
              <SidebarMenuItem key={item.href}>
                <SidebarMenuButton
                  asChild
                  isActive={!item.disabled && pathname === item.href}
                  tooltip={item.badge ? `${item.title} (${item.badge})` : item.title}
                  className={cn("h-8 text-[13px]", item.disabled && "opacity-40 pointer-events-none")}
                >
                  <Link to={item.disabled ? "#" : item.href}>
                    <item.icon className="size-3.5" />
                    <span>{item.title}</span>
                    {item.badge && (
                      <span className="ml-auto text-[10px] text-muted-foreground/60 font-normal">{item.badge}</span>
                    )}
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </Collapsible.Content>
      </SidebarMenuItem>
    </Collapsible.Root>
  );
}

export function AppSidebar() {
  const navigate = useNavigate();
  const { activeAccount } = useAccount();

  const platform: AdPlatform = activeAccount?.platform ?? "meta";
  const navGroups = useMemo(() => buildNavGroups(platform), [platform]);

  async function handleSignOut() {
    const supabase = createClient();
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast.error("Failed to sign out");
      return;
    }
    navigate("/login");
  }

  return (
    <Sidebar>
      <SidebarHeader className="border-b px-4 py-3 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Zap className="size-5 text-primary" />
            <div>
              <div className="font-semibold">SmartAds</div>
              <div className="text-xs text-muted-foreground">AI Ad Manager</div>
            </div>
          </div>
          <SidebarTrigger />
        </div>
        <AccountSwitcher />
      </SidebarHeader>

      <SidebarContent>
        <SidebarMenu className="px-2 py-2 space-y-0.5">
          {navGroups.map((group) => (
            <CollapsibleNavGroup key={group.label} group={group} />
          ))}
        </SidebarMenu>
      </SidebarContent>

      <SidebarFooter className="border-t">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton onClick={handleSignOut} tooltip="Sair">
              <LogOut />
              <span>Sair</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
